import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { loadPayengine } from 'payengine';
import { environment } from '../../environments/environment';

export interface PayEngineState {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  payEngine: any | null;
  sessionId: string | null;
  secureForm: any | null;
  secureFormReady: boolean;
}

@Injectable()
export class PayEngineService {
  private readonly config = environment.payengine;
  private payEngineState = new BehaviorSubject<PayEngineState>({
    isLoaded: false,
    isLoading: false,
    error: null,
    payEngine: null,
    sessionId: null,
    secureForm: null,
    secureFormReady: false
  });

  public state$: Observable<PayEngineState> = this.payEngineState.asObservable();

  constructor() {
    // Get session ID from localStorage on service initialization
    const sessionId = localStorage.getItem('payengineSessionId');
    if (sessionId) {
      this.updateState({ sessionId });
    }
  }

  /**
   * Initializes PayEngine if not already loaded or loading
   * Can be called multiple times safely
   */
  async initializePayEngine(): Promise<any> {
    const currentState = this.payEngineState.value;
    
    // Return existing PayEngine if already loaded
    if (currentState.isLoaded && currentState.payEngine) {
      return currentState.payEngine;
    }

    // Don't start multiple initializations
    if (currentState.isLoading) {
      return this.waitForInitialization();
    }

    // Check for session ID, create one for preview mode if needed
    let sessionId = localStorage.getItem('payengineSessionId');
    if (!sessionId) {
      if (location.search.includes('preview')) {
        sessionId = 'preview-session-id-' + Date.now();
        localStorage.setItem('payengineSessionId', sessionId);
        console.log('Created preview session ID:', sessionId);
      } else {
        const error = 'PayEngine session ID not found';
        this.updateState({ error, isLoading: false });
        throw new Error(error);
      }
    }

    try {
      this.updateState({ 
        isLoading: true, 
        error: null,
        sessionId 
      });

      console.log('PayEngine initialization started');
      const startTime = Date.now();
      
      const payEngine = await loadPayengine(this.config.PAYENGINE_LOADER_CONFIG);
      
      const initTime = (Date.now() - startTime) / 1000;
      console.log(`PayEngine initialized in ${initTime.toFixed(2)} seconds`);

      this.updateState({
        isLoaded: true,
        isLoading: false,
        payEngine,
        error: null
      });

      // Pre-create secure form for faster rendering
      this.createSecureForm(payEngine);

      return payEngine;
    } catch (error) {
      const errorMessage = 'Failed to initialize PayEngine';
      console.error(errorMessage, error);
      
      this.updateState({
        isLoaded: false,
        isLoading: false,
        error: errorMessage
      });
      
      throw error;
    }
  }

  /**
   * Wait for current initialization to complete
   */
  private waitForInitialization(): Promise<any> {
    return new Promise((resolve, reject) => {
      const subscription = this.state$.subscribe(state => {
        if (state.isLoaded && state.payEngine) {
          subscription.unsubscribe();
          resolve(state.payEngine);
        } else if (state.error && !state.isLoading) {
          subscription.unsubscribe();
          reject(new Error(state.error));
        }
      });
    });
  }

  /**
   * Get current PayEngine instance
   */
  getPayEngine(): any | null {
    return this.payEngineState.value.payEngine;
  }

  /**
   * Check if PayEngine is ready
   */
  isReady(): boolean {
    const state = this.payEngineState.value;
    return state.isLoaded && !!state.payEngine;
  }

  /**
   * Check if PayEngine is currently loading
   */
  isLoading(): boolean {
    return this.payEngineState.value.isLoading;
  }

  /**
   * Get current error if any
   */
  getError(): string | null {
    return this.payEngineState.value.error;
  }

  /**
   * Pre-create secure form for faster component rendering
   */
  private async createSecureForm(payEngine: any): Promise<void> {
    try {
      console.log('Pre-creating secure form...');
      const secureForm = await payEngine.SecureFields.create();
      
      this.updateState({
        secureForm,
        secureFormReady: true
      });
      
      console.log('Secure form pre-created successfully');
    } catch (error) {
      console.warn('Failed to pre-create secure form:', error);
      // Don't throw error as this is an optimization, not required functionality
    }
  }

  /**
   * Get pre-created secure form
   */
  getSecureForm(): any | null {
    return this.payEngineState.value.secureForm;
  }

  /**
   * Check if secure form is ready
   */
  isSecureFormReady(): boolean {
    return this.payEngineState.value.secureFormReady;
  }

  /**
   * Attach secure fields to DOM elements with predefined styling
   */
  attachSecureFields(form: any): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const css = {
          WebkitAppearance: 'none',
          paddingBottom: '16px',
          WebkitBoxShadow: 'none',
          boxShadow: 'none',
          lineHeight: '24px',
          fontSize: '16px',
          color: '#393C3C',
          padding: '0 12px',
          fontWeight: '400',
          margin: '0px',
          display: 'block',
          height: '48px',
          border: '1px solid #D8D9DA',
          borderRadius: '8px',
          fontFamily: '"Inter Variable", "SF Pro Text", "SF Pro Icons", -apple-system, BlinkMacSystemFont, Inter, system-ui, "Segoe UI", Roboto, Helvetica, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
          width: 'calc(100% - 27px)',
WebkitFontSmoothing: 'antialiased',
        };

        // Create all fields with styling
        form.field('#card-name', {
          type: 'text',
          name: 'card_holder',
          placeholder: 'Name on card',
          validations: ['required'],
          css,
        });

        form.field('#card-number', {
          type: 'card-number',
          name: 'card_number',
          successColor: '#4F8A10',
          errorColor: '#D8000C',
          placeholder: 'Credit Card Number',
          showCardIcon: false,
          validations: ['required', 'validCardNumber'],
          css,
        });

        form.field('#card-expiry', {
          type: 'card-expiration-date',
          name: 'card_exp',
          successColor: '#4F8A10',
          errorColor: '#D8000C',
          placeholder: 'MM / YY',
          validations: ['required', 'validCardExpirationDate'],
          css,
        });

        form.field('#card-cvc', {
          type: 'card-security-code',
          name: 'card_cvc',
          successColor: '#4F8A10',
          errorColor: '#D8000C',
          placeholder: 'CVC',
          maxLength: 4,
          validations: ['required', 'validCardSecurityCode'],
          css,
        });

        console.log('Secure fields attached to DOM');
        resolve();
      } catch (error) {
        console.error('Failed to attach secure fields:', error);
        reject(error);
      }
    });
  }

  /**
   * Reset the service state (useful for cleanup)
   */
  reset(): void {
    this.updateState({
      isLoaded: false,
      isLoading: false,
      error: null,
      payEngine: null,
      sessionId: localStorage.getItem('payengineSessionId'),
      secureForm: null,
      secureFormReady: false
    });
  }

  private updateState(updates: Partial<PayEngineState>): void {
    const currentState = this.payEngineState.value;
    this.payEngineState.next({
      ...currentState,
      ...updates
    });
  }
}


// WEBPACK FOOTER //
// ./src/app/services/payengine.service.ts